import axios from 'axios';

/**
 * Email service for sending lab reports via email
 */
class EmailService {
  constructor() {
    this.baseURL = process.env.REACT_APP_SERVER_BASE_URL || 'http://localhost:5001';
    this.apiPath = '/hospital-portal/api/email';
  }

  /**
   * Send lab report via email
   * @param {Object} reportData - Report data
   * @param {string} reportData.reportId - Report ID  
   * @param {string} reportData.patientId - Patient ID
   * @param {string} reportData.reportHtml - HTML content of the report
   * @param {string} reportData.reportType - Type of report (default: 'Lab Report')
   * @param {string} reportData.customEmail - Custom email (optional)
   * @param {string} token - JWT token
   * @returns {Promise<Object>} Response with job status
   */
  async sendLabReport(reportData, token) {
    try {
      const response = await axios.post(
        `${this.baseURL}${this.apiPath}/lab-report`,
        reportData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error sending lab report email:', error);
      
      return {
        success: false,
        error: error.response?.data?.error || error.message,
        details: error.response?.data || null
      };
    }
  }

  /**
   * Check email job status
   * @param {string} jobId - Job ID
   * @param {string} token - JWT token
   * @returns {Promise<Object>} Job status
   */
  async getEmailJobStatus(jobId, token) {
    try {
      const response = await axios.get(
        `${this.baseURL}${this.apiPath}/status/${jobId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error getting email job status:', error);
      
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }

  /**
   * Test email configuration
   * @param {string} token - JWT token
   * @returns {Promise<Object>} Configuration status
   */
  async testEmailConfig(token) {
    try {
      const response = await axios.get(
        `${this.baseURL}${this.apiPath}/test-config`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error testing email config:', error);
      
      return {
        success: false,
        error: error.response?.data?.error || error.message
      };
    }
  }
}

export default EmailService;
